import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const SuperAdminContext = createContext(null);

export const SuperAdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);

  const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('superAdminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    const storedAdmin = localStorage.getItem('superAdmin');
    const storedMustChange = localStorage.getItem('superAdminMustChangePassword');
    
    if (token && storedAdmin) {
      setAdmin(JSON.parse(storedAdmin));
      if (storedMustChange === 'true') {
        setMustChangePassword(true);
        setTempToken(token);
      }
      // Verify token is still valid
      api.get('/api/super-admin/auth/me')
        .then(res => setAdmin(res.data))
        .catch(() => {
          localStorage.removeItem('superAdminToken');
          localStorage.removeItem('superAdmin');
          localStorage.removeItem('superAdminMustChangePassword');
          setAdmin(null);
          setMustChangePassword(false);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/api/super-admin/auth/login', { email, password });
    // Only store credentials if 2FA is not required
    if (!res.data.requires_2fa) {
      localStorage.setItem('superAdminToken', res.data.access_token);
      localStorage.setItem('superAdmin', JSON.stringify(res.data.admin));
      
      // Check if password change is required
      if (res.data.must_change_password) {
        localStorage.setItem('superAdminMustChangePassword', 'true');
        setMustChangePassword(true);
        setTempToken(res.data.access_token);
      } else {
        localStorage.removeItem('superAdminMustChangePassword');
        setMustChangePassword(false);
      }
      
      setAdmin(res.data.admin);
    }
    return res.data;
  };

  const completePasswordChange = () => {
    // Called after successful forced password change
    localStorage.removeItem('superAdminMustChangePassword');
    setMustChangePassword(false);
    setTempToken(null);
  };

  const logout = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdmin');
    localStorage.removeItem('superAdminMustChangePassword');
    setAdmin(null);
    setMustChangePassword(false);
    setTempToken(null);
  };

  return (
    <SuperAdminContext.Provider value={{ 
      admin, 
      loading, 
      login, 
      logout, 
      api,
      mustChangePassword,
      tempToken,
      completePasswordChange
    }}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within SuperAdminProvider');
  }
  return context;
};
